import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtAED, fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import InvoiceView from '../components/InvoiceView';
import { Eye, Ban, Pencil, Printer } from 'lucide-react';
import PageHelp from '../components/PageHelp';

const STATUS_VARIANT = { open: 'secondary', preparing: 'default', ready: 'default', completed: 'outline', cancelled: 'destructive' };

export default function Orders() {
  const { currentOutlet } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [invoice, setInvoice] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);

  const load = async () => {
    const params = { limit: 200 };
    if (currentOutlet) params.outlet_id = currentOutlet.id;
    const { data } = await api.get('/orders', { params });
    setOrders(data);
  };
  useEffect(() => { load(); }, [currentOutlet]);

  const view = async (o) => {
    setDetails(o);
    if (o.invoice_no) {
      try {
        const invs = await api.get('/invoices', { params: { outlet_id: o.outlet_id } });
        const inv = invs.data.find((i) => i.order_id === o.id);
        setDetailInvoice(inv || null);
      } catch (e) { setDetailInvoice(null); }
    } else { setDetailInvoice(null); }
  };

  const filtered = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

  const voidOrder = async (o) => {
    const reason = window.prompt(`Void order ${o.order_no}? Enter a reason:`);
    if (!reason) return;
    try { await api.post(`/orders/${o.id}/cancel`, { reason }); load(); }
    catch (e) { window.alert(e.response?.data?.detail || 'Void failed (manager privilege may be required)'); }
  };

  const settle = (o) => navigate(`/pos?order=${o.id}&pay=1`);

  const reprint = async (o) => {
    try {
      const invs = await api.get('/invoices', { params: { outlet_id: o.outlet_id } });
      const inv = invs.data.find((i) => i.order_id === o.id);
      if (!inv) { window.alert('No invoice found for this order'); return; }
      setInvoice(inv);
      setTimeout(() => window.print(), 400);
    } catch (e) { window.alert('Could not load invoice'); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <h1 className="font-display text-2xl font-bold">Orders</h1>
      <PageHelp id="orders">All orders across POS, QR and aggregator channels. Filter by status. Open any completed order to view or reprint its VAT tax invoice.</PageHelp>
      <div className="flex gap-2 flex-wrap">
        {['all', 'open', 'preparing', 'ready', 'completed', 'cancelled'].map((s) => (
          <button key={s} data-testid={`order-filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm border capitalize ${filter === s ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>{s}</button>
        ))}
      </div>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Type</TableHead><TableHead>Channel</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Time</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.order_no}`}>
                <TableCell className="font-mono font-medium">{o.order_no}</TableCell>
                <TableCell className="capitalize">{o.type}</TableCell>
                <TableCell><Badge variant="secondary" className="capitalize">{o.channel}</Badge></TableCell>
                <TableCell><Badge variant={STATUS_VARIANT[o.status] || 'secondary'} className="capitalize">{o.status}</Badge></TableCell>
                <TableCell className="text-right font-mono">{fmtAED(o.grand_total)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1.5 justify-end flex-wrap">
                    <Button size="sm" variant="outline" className="h-8" data-testid={`order-view-${o.order_no}`} onClick={() => view(o)}><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                    {o.invoice_no && <Button size="sm" variant="outline" className="h-8" data-testid={`order-reprint-${o.order_no}`} onClick={() => reprint(o)}><Printer className="h-3.5 w-3.5 mr-1" /> Reprint</Button>}
                    {!['completed', 'cancelled'].includes(o.status) && <Button size="sm" variant="outline" className="h-8" data-testid={`order-edit-${o.order_no}`} onClick={() => navigate(`/pos?order=${o.id}`)}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
                    {!['completed', 'cancelled'].includes(o.status) && <Button size="sm" className="h-8" data-testid={`order-settle-${o.order_no}`} onClick={() => settle(o)}>Settle</Button>}
                    {!['completed', 'cancelled'].includes(o.status) && <Button size="sm" variant="outline" className="h-8 text-destructive border-destructive/40 hover:bg-destructive/10" data-testid={`order-void-${o.order_no}`} onClick={() => voidOrder(o)}><Ban className="h-3.5 w-3.5 mr-1" /> Void</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <div className="p-8 text-center text-muted-foreground">No orders</div>}
      </Card>
      <Dialog open={!!invoice} onOpenChange={(o) => !o && setInvoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tax Invoice</DialogTitle></DialogHeader>
          {invoice && <InvoiceView invoice={invoice} />}
          <div className="flex gap-2">
            <Button data-testid="invoice-reprint-button" className="flex-1" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" /> Print</Button>
            <Button variant="outline" className="flex-1" onClick={() => setInvoice(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!details} onOpenChange={(o) => { if (!o) { setDetails(null); setDetailInvoice(null); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="order-details-dialog">
          <DialogHeader><DialogTitle>Order {details?.order_no}</DialogTitle></DialogHeader>
          {details && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="capitalize">{details.type}</Badge>
                <Badge variant="secondary" className="capitalize">{details.channel}</Badge>
                <Badge variant={STATUS_VARIANT[details.status] || 'secondary'} className="capitalize">{details.status}</Badge>
              </div>
              {details.delivery_address && <div className="text-muted-foreground"><b className="text-foreground">Delivery:</b> {details.delivery_address}</div>}
              {details.customer_phone && <div className="text-muted-foreground"><b className="text-foreground">Phone:</b> {details.customer_phone}</div>}
              {details.notes && <div className="text-muted-foreground"><b className="text-foreground">Notes:</b> {details.notes}</div>}
              <div className="border rounded-lg divide-y">
                {(details.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between px-3 py-2" data-testid={`order-detail-line-${i}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{it.qty} × {it.name}</div>
                      {it.modifiers?.length > 0 && <div className="text-[11px] text-muted-foreground">{it.modifiers.map((m) => m.name).join(', ')}</div>}
                      {it.notes && <div className="text-[11px] text-muted-foreground italic">“{it.notes}”</div>}
                    </div>
                    <div className="font-mono shrink-0 pl-2">{fmtAED((it.unit_price || 0) * (it.qty || 0))}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">{fmtAED(details.subtotal)}</span></div>
                {details.discount > 0 && <div className="flex justify-between text-destructive"><span>Discount</span><span className="font-mono">-{fmtAED(details.discount)}</span></div>}
                <div className="flex justify-between text-muted-foreground"><span>VAT (5%)</span><span className="font-mono">{fmtAED(details.vat_total)}</span></div>
                {details.service_charge > 0 && <div className="flex justify-between text-muted-foreground"><span>Service</span><span className="font-mono">{fmtAED(details.service_charge)}</span></div>}
                <div className="flex justify-between font-bold text-base pt-1"><span>Total</span><span className="font-mono">{fmtAED(details.grand_total)}</span></div>
              </div>
              {detailInvoice ? (
                <div className="pt-2 border-t"><InvoiceView invoice={detailInvoice} /></div>
              ) : (
                !['completed', 'cancelled'].includes(details.status) && (
                  <Button className="w-full" data-testid="order-details-edit" onClick={() => navigate(`/pos?order=${details.id}`)}><Pencil className="h-4 w-4 mr-1" /> Edit this order</Button>
                )
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
